/**
 * AGC: WelcomePage Component - Complete game introduction and tutorial
 * AGC: Reuses existing game logic and provides comprehensive game guide
 */

import React, { useState } from 'react'
import { ArrowLeft, Swords, Zap, Shield, Snowflake, Users, Bot, BookOpen, Code, Wallet, Trophy } from 'lucide-react'

interface WelcomePageProps {
  onBackToMenu: () => void
  onStartPlaying: () => void
}

export const WelcomePage: React.FC<WelcomePageProps> = ({ onBackToMenu, onStartPlaying }) => {
  const [activeTab, setActiveTab] = useState('overview')
  const buffContractAddress = import.meta.env.VITE_BUFF_1155_ADDRESS || '0x0000000000000000000000000000000000000000'

  const renderContent = () => {
    switch (activeTab) {
       case 'overview':
         return (
           <div className="space-y-6 sm:space-y-8">
             <h3 className="text-2xl sm:text-3xl font-bold text-blue-400 mb-4">
               Welcome to Origami Battles!
             </h3>
             <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
               Imagine a world where classic Rock-Paper-Scissors meets deep strategic combat and modern blockchain innovation. Origami Battles lets you command digital origami warriors in epic battles and earn real value <span className="font-semibold" style={{ color: '#1652F0' }}>ONCHAIN</span>.
             </p>
             <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
               It's easy to learn but offers meaningful depth for competitive play. Win battles to <span className="text-yellow-400 font-semibold">EARN OGB Tokens & XP </span><span className="font-semibold" style={{ color: '#1652F0' }}>ONCHAIN</span>, and collect NFT buffs that you truly own.
             </p>

             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8">
               <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600 text-center">
                 <div className="text-4xl sm:text-5xl mb-3">⚔️</div>
                 <h4 className="text-lg sm:text-xl font-bold text-blue-400 mb-2">Strategic Combat</h4>
                 <p className="text-gray-400 text-xs sm:text-sm">Master elements & buffs</p>
               </div>
               <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600 text-center">
                 <div className="text-4xl sm:text-5xl mb-3">💰</div>
                 <h4 className="text-lg sm:text-xl font-bold text-green-400 mb-2">Real Rewards (<span style={{ color: '#1652F0' }}>ONCHAIN</span>)</h4>
                 <p className="text-gray-400 text-xs sm:text-sm">Earn OGB Tokens & XP <span style={{ color: '#1652F0' }}>ONCHAIN</span></p>
               </div>
               <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600 text-center sm:col-span-2 lg:col-span-1">
                 <div className="text-4xl sm:text-5xl mb-3">💎</div>
                 <h4 className="text-lg sm:text-xl font-bold text-purple-400 mb-2">NFT Ownership</h4>
                 <p className="text-gray-400 text-xs sm:text-sm">Collect & trade buffs</p>
               </div>
             </div>
           </div>
         )
      case 'mechanics':
        return (
          <div className="space-y-6 sm:space-y-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-green-400 mb-4">
              Game Mechanics: Master the Art of Battle
            </h3>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
              At its core, Origami Battles is a strategic Rock-Paper-Scissors game enhanced with elemental combat and powerful NFT buffs. Every turn, you choose an origami hand and an element, aiming to outsmart your opponent.
            </p>

            {/* Origami Choices */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-gray-200 mb-4">
                1. Origami Choices
              </h4>
              <div className="flex flex-col sm:flex-row justify-around items-center text-center text-gray-300 gap-4 sm:gap-0">
                <div>
                  <img src="/rock.png" alt="Rock" className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 mx-auto mb-2 object-contain" />
                  <p className="text-sm sm:text-base">Rock beats Scissors</p>
                </div>
                <div>
                  <img src="/paper.png" alt="Paper" className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 mx-auto mb-2 object-contain" />
                  <p className="text-sm sm:text-base">Paper beats Rock</p>
                </div>
                <div>
                  <img src="/scissor.png" alt="Scissors" className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 mx-auto mb-2 object-contain" />
                  <p className="text-sm sm:text-base">Scissors beats Paper</p>
                </div>
              </div>
            </div>

            {/* Elemental Cycle */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-cyan-400 mb-4">
                2. Elemental Combat
              </h4>
              <p className="text-gray-300 mb-4 text-sm sm:text-base">
                Add another layer of strategy with our elemental cycle:
              </p>
              <div className="text-center text-2xl sm:text-3xl font-semibold text-cyan-400 flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
                <span>💧</span>
                <span className="text-gray-500">›</span>
                <span>🔥</span>
                <span className="text-gray-500">›</span>
                <span>❄️</span>
                <span className="text-gray-500">›</span>
                <span>💨</span>
                <span className="text-gray-500">›</span>
                <span>💧</span>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm mt-4">
                Choose the advantageous element for +10 damage. Neutral elements incur a -5 penalty, while matching elements result in a draw (0 bonus/penalty).
              </p>
            </div>

            {/* NFT Buffs */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-purple-400 mb-4">
                3. Powerful NFT Buffs
              </h4>
              <p className="text-gray-300 mb-4 text-sm sm:text-base">
                Collect and equip unique NFT buffs to turn the tide of battle:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-center">
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
                  <span className="font-semibold text-gray-200 text-sm sm:text-base">Shield:</span> <span className="text-gray-400 text-xs sm:text-sm">25% Damage Reduction</span>
                </div>
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <Swords className="w-5 h-5 sm:w-6 sm:h-6 text-red-400" />
                  <span className="font-semibold text-gray-200 text-sm sm:text-base">Pierce:</span> <span className="text-gray-400 text-xs sm:text-sm">+25% Attack Power</span>
                </div>
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
                  <span className="font-semibold text-gray-200 text-sm sm:text-base">Haste:</span> <span className="text-gray-400 text-xs sm:text-sm">+5 Flat Damage</span>
                </div>
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <Snowflake className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
                  <span className="font-semibold text-gray-200 text-sm sm:text-base">Frost:</span> <span className="text-gray-400 text-xs sm:text-sm">Freeze Opponent (1 Turn)</span>
                </div>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm mt-4">
                Buffs have specific activation rates and can be combined for powerful synergies.
              </p>
            </div>

            {/* Rarity System */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-yellow-400 mb-4">
                4. 💎 Rarity System
              </h4>
              <p className="text-gray-300 mb-4 text-sm sm:text-base">
                Every turn, luck determines your rarity bonus for extra damage:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-center">
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <span className="text-2xl">💎</span>
                  <span className="font-semibold text-gray-200 text-sm sm:text-base">Diamond:</span> <span className="text-gray-400 text-xs sm:text-sm">+50 Damage (0.1%)</span>
                </div>
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <span className="text-2xl">🥇</span>
                  <span className="font-semibold text-gray-200 text-sm sm:text-base">Gold:</span> <span className="text-gray-400 text-xs sm:text-sm">+25 Damage (1%)</span>
                </div>
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <span className="text-2xl">🥈</span>
                  <span className="font-semibold text-gray-200 text-sm sm:text-base">Silver:</span> <span className="text-gray-400 text-xs sm:text-sm">+10 Damage (5%)</span>
                </div>
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <span className="text-2xl">🥉</span>
                  <span className="font-semibold text-gray-200 text-sm sm:text-base">Bronze:</span> <span className="text-gray-400 text-xs sm:text-sm">+5 Damage (15%)</span>
                </div>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm mt-4">
                Rarity is determined by RNG on every turn - even the best strategy can be enhanced by luck!
              </p>
            </div>

            {/* Leveling System */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-green-400 mb-4">
                5. 📈 Leveling System
              </h4>
              <p className="text-gray-300 mb-4 text-sm sm:text-base">
                Gain experience and level up to increase your combat effectiveness:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⚔️</span>
                  <div>
                    <span className="font-semibold text-gray-200 text-sm sm:text-base">Attack Power:</span>
                    <span className="text-gray-400 text-xs sm:text-sm ml-2">+1 per level (base damage)</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🛡️</span>
                  <div>
                    <span className="font-semibold text-gray-200 text-sm sm:text-base">Defense:</span>
                    <span className="text-gray-400 text-xs sm:text-sm ml-2">+0.5 per level (damage reduction)</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💪</span>
                  <div>
                    <span className="font-semibold text-gray-200 text-sm sm:text-base">Health:</span>
                    <span className="text-gray-400 text-xs sm:text-sm ml-2">+10 per level (max energy)</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🎯</span>
                  <div>
                    <span className="font-semibold text-gray-200 text-sm sm:text-base">Experience:</span>
                    <span className="text-gray-400 text-xs sm:text-sm ml-2">Earned by winning battles and completing challenges</span>
                  </div>
                </div>
              </div>

              {/* XP Calculation Details */}
              <div className="mt-6 pt-4 border-t border-slate-600">
                <h5 className="text-lg sm:text-xl font-bold text-yellow-400 mb-3">
                  💡 How XP is Calculated
                </h5>
                <p className="text-gray-300 mb-4 text-sm sm:text-base">
                  Your XP reward depends on both the battle outcome and your opponent's level:
                </p>

                {/* Base XP */}
                <div className="bg-slate-700 rounded-lg p-3 sm:p-4 mb-4">
                  <h6 className="font-bold text-cyan-400 mb-2 text-sm sm:text-base">Base XP by Outcome (PvP):</h6>
                  <ul className="list-disc list-inside text-gray-300 space-y-1 text-xs sm:text-sm ml-2">
                    <li><strong className="text-green-400">Win:</strong> 50 XP</li>
                    <li><strong className="text-yellow-400">Draw:</strong> 25 XP</li>
                    <li><strong className="text-red-400">Loss:</strong> 15 XP</li>
                  </ul>
                  <p className="text-gray-400 text-xs mt-2">
                    ⚠️ <strong>PvE Battles:</strong> All XP rewards are <strong className="text-yellow-400">50% of PvP values</strong> (Win: 25 XP, Draw: 12-13 XP, Loss: 7-8 XP)
                  </p>
                </div>

                {/* Level Difference Multipliers */}
                <div className="bg-slate-700 rounded-lg p-3 sm:p-4 mb-4">
                  <h6 className="font-bold text-purple-400 mb-2 text-sm sm:text-base">Level Difference Multipliers:</h6>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-green-400 font-bold">↑</span>
                      <div>
                        <strong className="text-gray-200">Fighting Higher Level Opponents:</strong>
                        <span className="text-gray-300 ml-2">+10% XP per level above you</span>
                        <p className="text-gray-400 text-xs mt-1 ml-4">
                          Example: Level 5 vs Level 8 = +30% bonus (base XP × 1.3)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400 font-bold">=</span>
                      <div>
                        <strong className="text-gray-200">Same Level:</strong>
                        <span className="text-gray-300 ml-2">No multiplier (base XP × 1.0)</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-red-400 font-bold">↓</span>
                      <div>
                        <strong className="text-gray-200">Fighting Lower Level Opponents:</strong>
                        <span className="text-gray-300 ml-2">-5% XP per level below you (minimum 50% of base XP)</span>
                        <p className="text-gray-400 text-xs mt-1 ml-4">
                          Example: Level 8 vs Level 5 = -15% penalty (base XP × 0.85, min 50%)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* XP Calculation Examples */}
                <div className="bg-slate-700 rounded-lg p-3 sm:p-4">
                  <h6 className="font-bold text-blue-400 mb-2 text-sm sm:text-base">📊 XP Calculation Examples:</h6>
                  <div className="space-y-3 text-xs sm:text-sm">
                    <div className="bg-slate-600 rounded p-2">
                      <p className="text-gray-200 mb-1">
                        <strong>Level 5 Player</strong> <span className="text-green-400">WINS</span> vs <strong>Level 8 Opponent</strong>:
                      </p>
                      <p className="text-gray-300">
                        Base: 50 XP × 1.3 (3 levels above) = <span className="text-green-400 font-bold">65 XP</span>
                      </p>
                    </div>
                    <div className="bg-slate-600 rounded p-2">
                      <p className="text-gray-200 mb-1">
                        <strong>Level 8 Player</strong> <span className="text-green-400">WINS</span> vs <strong>Level 5 Opponent</strong>:
                      </p>
                      <p className="text-gray-300">
                        Base: 50 XP × 0.85 (3 levels below) = <span className="text-yellow-400 font-bold">42 XP</span>
                      </p>
                    </div>
                    <div className="bg-slate-600 rounded p-2">
                      <p className="text-gray-200 mb-1">
                        <strong>Level 10 Player</strong> <span className="text-red-400">LOSES</span> vs <strong>Level 12 Opponent</strong>:
                      </p>
                      <p className="text-gray-300">
                        Base: 15 XP × 1.2 (2 levels above) = <span className="text-yellow-400 font-bold">18 XP</span>
                      </p>
                    </div>
                    <div className="bg-slate-600 rounded p-2">
                      <p className="text-gray-200 mb-1">
                        <strong>Level 7 Player</strong> <span className="text-yellow-400">DRAWS</span> vs <strong>Level 7 Opponent</strong>:
                      </p>
                      <p className="text-gray-300">
                        Base: 25 XP × 1.0 (same level) = <span className="text-yellow-400 font-bold">25 XP</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-gray-400 text-xs sm:text-sm mt-4">
                Higher levels provide significant advantages in battle - every level matters! Challenge higher-level opponents for maximum XP rewards! 💪
              </p>
            </div>
          </div>
        )
      case 'blockchain':
        return (
          <div className="space-y-6 sm:space-y-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-purple-400 mb-4">
              Blockchain Integration: True Ownership & Rewards
            </h3>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
              Origami Battles leverages the power of blockchain technology to provide true ownership of in-game assets and transparent, verifiable rewards.
            </p>

            {/* Backend vs Blockchain */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-indigo-400 mb-4">
                Backend vs. Blockchain: The Perfect Partnership
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <h5 className="font-bold text-indigo-400 mb-2 flex items-center gap-2 text-sm sm:text-base"><Code className="w-4 h-4 sm:w-5 sm:h-5" /> Backend (Game Engine)</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-1 text-xs sm:text-sm">
                    <li>Real-time matchmaking & turn management</li>
                    <li>Complex battle calculations (damage, buffs)</li>
                    <li>Fast, responsive user experience</li>
                    <li>Permanent battle logs (Firebase)</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-bold text-purple-400 mb-2 flex items-center gap-2 text-sm sm:text-base"><Wallet className="w-4 h-4 sm:w-5 sm:h-5" /> Blockchain (Trust Layer)</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-1 text-xs sm:text-sm">
                    <li>OGB token transactions & rewards</li>
                    <li>NFT ownership (ERC-1155 Buffs)</li>
                    <li>Verifiable battle results & XP tracking</li>
                    <li>Transparent, tamper-proof records</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Tokenomics */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-yellow-400 mb-4">
                Deflationary Tokenomics: OGB Token
              </h4>
              <p className="text-gray-300 mb-4 text-sm sm:text-base">
                Our native token, OGB, is at the heart of the game's economy:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 text-xs sm:text-sm">
                <li><strong>Entry Fee</strong>: 50 OGB per battle</li>
                <li><strong>Winner</strong>: Receives 75 OGB (50 + 25 from loser)</li>
                <li><strong>Loser</strong>: 25 OGB burned (deflationary mechanism)</li>
                <li><strong>Draw</strong>: Both get 25 OGB refund, 50 OGB burned</li>
                <li><strong>XP Rewards</strong>: Awarded <span style={{ color: '#1652F0' }}>on-chain</span> for every battle</li>
              </ul>
              <p className="text-gray-400 text-xs sm:text-sm mt-4">
                This system ensures scarcity, rewards skill, and provides real value to your victories.
              </p>
            </div>

            {/* NFT System */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-purple-400 mb-4">
                💎 NFT Buff System (ERC-1155)
              </h4>
              <p className="text-gray-300 mb-4 text-sm sm:text-base">
                Origami Battles uses the ERC-1155 standard for buff NFTs, allowing true ownership and tradeability:
              </p>
              
              {/* Contract Info */}
              <div className="bg-slate-700 rounded-lg p-3 mb-4 border border-slate-500">
                <h5 className="font-bold text-cyan-400 mb-2 text-sm sm:text-base">🔗 Smart Contract</h5>
                <p className="text-gray-300 mb-2 text-xs sm:text-sm">
                  <strong>Contract Address:</strong>{' '}
                  <code className="text-cyan-300 text-xs break-all">{buffContractAddress}</code>
                </p>
                <p className="text-gray-300 text-xs sm:text-sm">
                  <strong>Standard:</strong> ERC-1155 (Multi-Token Standard) - Allows multiple token types in a single contract
                </p>
              </div>

              {/* Buff Types */}
              <div className="bg-slate-700 rounded-lg p-3 mb-4 border border-slate-500">
                <h5 className="font-bold text-blue-400 mb-2 text-sm sm:text-base">🛡️ Buff Types (NFT IDs)</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-400" />
                    <span className="text-gray-300"><strong>ID 0 - Shield:</strong> 25% damage reduction</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Swords className="w-4 h-4 text-red-400" />
                    <span className="text-gray-300"><strong>ID 1 - Pierce:</strong> +25% attack power</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    <span className="text-gray-300"><strong>ID 2 - Haste:</strong> +5 flat damage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Snowflake className="w-4 h-4 text-cyan-400" />
                    <span className="text-gray-300"><strong>ID 3 - Frost:</strong> Freeze opponent (1 turn)</span>
                  </div>
                </div>
              </div>

              {/* NFT Features */}
              <div className="bg-slate-700 rounded-lg p-3 mb-4 border border-slate-500">
                <h5 className="font-bold text-green-400 mb-2 text-sm sm:text-base">✨ NFT Features</h5>
                <ul className="list-disc list-inside text-gray-300 space-y-1 text-xs sm:text-sm ml-2">
                  <li><strong>True Ownership:</strong> NFTs are stored in your wallet, you fully control them</li>
                  <li><strong>Tradeable:</strong> Transfer, sell, or gift your buff NFTs to other players</li>
                  <li><strong>Purchase Options:</strong> Buy with USDC (5 USDC), ETH (0.001 ETH), or OGB (1000 OGB)</li>
                  <li><strong>Battle Usage:</strong> Equip up to 2 buffs per battle for strategic advantages</li>
                  <li><strong>Consumable:</strong> Buffs are burned when used in PvE battles (tradeable in PvP)</li>
                  <li><strong>Verifiable:</strong> All ownership and transactions are <span style={{ color: '#1652F0' }}>on-chain</span> and transparent</li>
                </ul>
              </div>

              {/* Marketplace Integration */}
              <div className="bg-slate-700 rounded-lg p-3 border border-slate-500">
                <h5 className="font-bold text-yellow-400 mb-2 text-sm sm:text-base">🏪 Marketplace Integration</h5>
                <p className="text-gray-300 mb-2 text-xs sm:text-sm">
                  Purchase buff NFTs directly from the in-game marketplace using multiple payment methods:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs sm:text-sm">
                  <div className="bg-slate-600 rounded p-2 text-center">
                    <p className="text-gray-200 font-semibold">USDC</p>
                    <p className="text-gray-300">5 USDC per buff</p>
                  </div>
                  <div className="bg-slate-600 rounded p-2 text-center">
                    <p className="text-gray-200 font-semibold">ETH</p>
                    <p className="text-gray-300">0.001 ETH per buff</p>
                  </div>
                  <div className="bg-slate-600 rounded p-2 text-center">
                    <p className="text-gray-200 font-semibold">OGB</p>
                    <p className="text-gray-300">1000 OGB per buff</p>
                  </div>
                </div>
                <p className="text-gray-400 text-xs mt-3">
                  💡 <strong>Tip:</strong> All NFT purchases are <span style={{ color: '#1652F0' }}>on-chain</span> transactions, ensuring full transparency and ownership verification on BaseScan.
                </p>
              </div>
            </div>

            {/* ONCHAIN MAXI - PvE Onchain Step-by-step */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-green-400 mb-4"><span style={{ color: '#1652F0' }}>ONCHAIN</span> MAXI: PvE <span style={{ color: '#1652F0' }}>On-Chain</span> Flow</h4>
              
              <div className="space-y-4 text-xs sm:text-sm">
                <div className="bg-slate-700 rounded-lg p-3 border border-slate-500">
                  <h5 className="font-bold text-cyan-400 mb-2">🔗 Smart Contract Architecture</h5>
                  <p className="text-gray-300 mb-2">Uses <code className="text-cyan-300">PvEBattleManagerV4.sol</code> with deterministic bot AI, transparent damage calculations, and immutable game state. All logic executes in EVM bytecode with zero backend dependencies.</p>
                </div>

                <div className="bg-slate-700 rounded-lg p-3 border border-slate-500">
                  <h5 className="font-bold text-blue-400 mb-2">💰 Payment & Authorization Flow</h5>
                  <ol className="list-decimal list-inside text-gray-300 space-y-1 ml-2">
                    <li><strong>ERC-20 Approval</strong>: <code>approve(manager, 50e18)</code> for OGB tokens</li>
                    <li><strong>ERC-1155 Approval</strong>: <code>setApprovalForAll(manager, true)</code> for NFT buffs</li>
                    <li><strong>Battle Creation</strong>: <code>createBattle(player, bot, stake)</code> generates unique battleId</li>
                    <li><strong>Stake Payment</strong>: <code>payBattleStakeWithNFTs(battleId, ogbTokenAddress, buff1155Address, nftIds)</code> burns NFTs immediately</li>
                    <li><strong>Bot Auto-Payment</strong>: Contract-funded bot pays 50 OGB via <code>payForBattle()</code></li>
                  </ol>
                </div>

                <div className="bg-slate-700 rounded-lg p-3 border border-slate-500">
                  <h5 className="font-bold text-purple-400 mb-2">⚔️ <span style={{ color: '#1652F0' }}>On-Chain</span> Battle Execution</h5>
                  <ol className="list-decimal list-inside text-gray-300 space-y-1 ml-2">
                    <li><strong>Turn Submission</strong>: <code>playTurn(battleId, hand, element)</code> with gas fee</li>
                    <li><strong>Bot AI Response</strong>: <code>getBotChoice(battleId, level)</code> uses <code>keccak256(seed)</code> for deterministic randomness</li>
                    <li><strong>Damage Calculation</strong>: <code>calculateTurnDamage()</code> runs complex game logic in EVM</li>
                    <li><strong>State Update</strong>: HP, turn counter, buff activations stored in contract storage</li>
                    <li><strong>Event Emission</strong>: <code>TurnPlayed</code> event logs all turn data for transparency</li>
                  </ol>
                </div>

                <div className="bg-slate-700 rounded-lg p-3 border border-slate-500">
                  <h5 className="font-bold text-yellow-400 mb-2">🏆 Resolution & Rewards</h5>
                  <ol className="list-decimal list-inside text-gray-300 space-y-1 ml-2">
                    <li><strong>Win Condition Check</strong>: Contract verifies HP=0 or max turns reached</li>
                    <li><strong>Reward Distribution</strong>: Winner gets 75 OGB, loser's 25 OGB burned (deflationary)</li>
                    <li><strong>XP Calculation</strong>: <span style={{ color: '#1652F0' }}>On-chain</span> XP tracking with level progression</li>
                    <li><strong>Event Logging</strong>: <code>BattleResolved</code> or <code>BattleDraw</code> events emitted</li>
                    <li><strong>State Finalization</strong>: Battle marked as resolved, all data immutable</li>
                  </ol>
                </div>

                <div className="bg-slate-700 rounded-lg p-3 border border-slate-500">
                  <h5 className="font-bold text-red-400 mb-2">🔍 Verification & Transparency</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-1 ml-2">
                    <li><strong>BaseScan Verification</strong>: Every transaction, event, and state change visible</li>
                    <li><strong>Contract Source Code</strong>: Open source, auditable Solidity code</li>
                    <li><strong>Deterministic Bot AI</strong>: Bot choices reproducible using same seed</li>
                    <li><strong>Gas Cost Transparency</strong>: ~$2-5 total gas fees for complete battle</li>
                    <li><strong>No Backend Dependency</strong>: 100% <span style={{ color: '#1652F0' }}>on-chain</span> execution, zero trust required</li>
                  </ul>
                </div>

                <div className="bg-slate-600 rounded-lg p-2 border border-slate-400">
                  <p className="text-xs text-gray-300 text-center">
                    <strong style={{ color: '#1652F0' }}>ONCHAIN MAXI</strong> = Every calculation, every random number, every damage point is verifiable, immutable, and transparent on the blockchain. No hidden logic, no backend manipulation, no trust required.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      case 'modes':
        return (
          <div className="space-y-6 sm:space-y-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-orange-400 mb-4">
              Battle Modes: Choose Your Path to Glory
            </h3>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
              Origami Battles offers diverse modes to suit every player's preference, from casual practice to intense competitive play.
            </p>

            {/* PvE On-Chain */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-blue-400 mb-4 flex items-center gap-2">
                <Bot className="w-5 h-5 sm:w-6 sm:h-6" /> PvE <span style={{ color: '#1652F0' }}>On-Chain</span> Mode
              </h4>
              <ul className="list-disc list-inside text-gray-300 space-y-1 text-xs sm:text-sm">
                <li><strong>Experience</strong>: Full blockchain integration, higher rewards, permanent records.</li>
                <li><strong>Technology</strong>: Backend for game logic, Blockchain for battle creation, results, rewards.</li>
                <li><strong>Best For</strong>: Players seeking maximum rewards and true ownership.</li>
              </ul>
            </div>

            {/* PvE Hybrid */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-green-400 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6" /> PvE Hybrid Mode
              </h4>
              <ul className="list-disc list-inside text-gray-300 space-y-1 text-xs sm:text-sm">
                <li><strong>Experience</strong>: Lower entry cost, same rewards, practice without high stakes.</li>
                <li><strong>Technology</strong>: Backend handles most, Blockchain records final results/rewards only.</li>
                <li><strong>Best For</strong>: New players learning the ropes, risk-free experimentation.</li>
              </ul>
            </div>

            {/* PvP Mode */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-red-400 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 sm:w-6 sm:h-6" /> PvP Mode
              </h4>
              <ul className="list-disc list-inside text-gray-300 space-y-1 text-xs sm:text-sm">
                <li><strong>Experience</strong>: Real players, fair matchmaking, highest rewards, ranked leaderboard.</li>
                <li><strong>Technology</strong>: Backend for matchmaking/real-time, Blockchain for payments/results.</li>
                <li><strong>Best For</strong>: Competitive players seeking glory and top rewards.</li>
              </ul>
            </div>
          </div>
        )
      case 'tournaments':
        return (
          <div className="space-y-6 sm:space-y-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 sm:w-8 sm:h-8" /> Tournaments: Compete for Glory
            </h3>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
              Tournaments are competitive events where players battle in a round-robin format to determine the ultimate champion. 
              Compete against multiple opponents, climb the leaderboard, and earn exclusive rewards!
            </p>

            {/* Tournament Rules */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-blue-400 mb-4">
                📋 Tournament Rules & Format
              </h4>
              <div className="space-y-4">
                <div className="bg-slate-700 rounded-lg p-4 border border-slate-500">
                  <h5 className="font-bold text-cyan-400 mb-3 text-sm sm:text-base">👥 Player Requirements</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 text-xs sm:text-sm">
                    <li><strong>Minimum Players:</strong> 3 players required to start</li>
                    <li><strong>Maximum Players:</strong> 10 players per tournament</li>
                    <li><strong>Format:</strong> Round-robin (every player faces every other player)</li>
                    <li><strong>Auto-Start:</strong> Tournament begins automatically when minimum players join</li>
                  </ul>
                </div>

                <div className="bg-slate-700 rounded-lg p-4 border border-slate-500">
                  <h5 className="font-bold text-green-400 mb-3 text-sm sm:text-base">⚔️ Battle Structure</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 text-xs sm:text-sm">
                    <li>Each player battles every other player exactly once</li>
                    <li>Standard battle rules apply (Rock-Paper-Scissors + Elements + Buffs)</li>
                    <li>Battles are best-of-4 turns (first to 0 energy loses)</li>
                    <li>All battles use the same game mechanics as regular PvP</li>
                  </ul>
                </div>

                <div className="bg-slate-700 rounded-lg p-4 border border-slate-500">
                  <h5 className="font-bold text-purple-400 mb-3 text-sm sm:text-base">🏆 Scoring System</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 text-xs sm:text-sm">
                    <li><strong>Win:</strong> +1 win, 0 losses</li>
                    <li><strong>Loss:</strong> 0 wins, +1 loss</li>
                    <li><strong>Draw:</strong> Both players get +1 draw (rare but possible)</li>
                    <li>Final standings determined by total wins, then tiebreakers</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Payment System */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-green-400 mb-4">
                💰 Payment System
              </h4>
              <p className="text-gray-300 mb-4 text-sm sm:text-base">
                Tournament entry requires payment for each battle you participate in. Here's how it works:
              </p>

              <div className="space-y-4">
                <div className="bg-slate-700 rounded-lg p-4 border border-slate-500">
                  <h5 className="font-bold text-yellow-400 mb-3 text-sm sm:text-base">💳 Entry Fee</h5>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <p className="text-gray-300">
                      <strong>Per Battle:</strong> 50 OGB tokens required to participate
                    </p>
                    <p className="text-gray-300">
                      <strong>Payment Timing:</strong> You pay when both you and your opponent click "Play" to start the match
                    </p>
                    <p className="text-gray-300">
                      <strong>Payment Method:</strong> OGB tokens are deducted from your wallet <span style={{ color: '#1652F0' }}>on-chain</span>
                    </p>
                  </div>
                </div>

                <div className="bg-slate-700 rounded-lg p-4 border border-slate-500">
                  <h5 className="font-bold text-blue-400 mb-3 text-sm sm:text-base">🎮 Battle Payment Flow</h5>
                  <ol className="list-decimal list-inside text-gray-300 space-y-2 text-xs sm:text-sm ml-2">
                    <li>Tournament match is created when both players are ready</li>
                    <li>Both players must click "Play" to confirm participation</li>
                    <li>Once both players click, each pays 50 OGB <span style={{ color: '#1652F0' }}>on-chain</span></li>
                    <li>Battle begins immediately after both payments are confirmed</li>
                    <li>Winner receives 75 OGB, loser's 25 OGB is burned (same as regular PvP)</li>
                  </ol>
                </div>

                <div className="bg-slate-700 rounded-lg p-4 border border-slate-500">
                  <h5 className="font-bold text-red-400 mb-3 text-sm sm:text-base">⚠️ Important Payment Notes</h5>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 text-xs sm:text-sm ml-2">
                    <li>You must have sufficient OGB balance before joining a tournament</li>
                    <li>Payment is required for EACH battle in the tournament (not a one-time entry fee)</li>
                    <li>If you can't pay for a match, you'll forfeit that battle</li>
                    <li>All payments are <span style={{ color: '#1652F0' }}>on-chain</span> and verifiable on BaseScan</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Tie-Breaking System */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-purple-400 mb-4">
                🏅 Tie-Breaking System
              </h4>
              <p className="text-gray-300 mb-4 text-sm sm:text-base">
                When players have the same number of wins, the following tie-breakers determine final rankings:
              </p>

              <div className="bg-slate-700 rounded-lg p-4 border border-slate-500">
                <h5 className="font-bold text-cyan-400 mb-4 text-sm sm:text-base">📊 Ranking Priority (In Order)</h5>
                <div className="space-y-4">
                  <div className="bg-slate-600 rounded-lg p-3 border-l-4 border-blue-500">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl font-bold text-blue-400">1</span>
                      <h6 className="font-bold text-blue-300 text-sm sm:text-base">Total Wins</h6>
                    </div>
                    <p className="text-gray-300 text-xs sm:text-sm ml-8">
                      Players with more wins rank higher. This is the primary ranking factor.
                    </p>
                  </div>

                  <div className="bg-slate-600 rounded-lg p-3 border-l-4 border-green-500">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl font-bold text-green-400">2</span>
                      <h6 className="font-bold text-green-300 text-sm sm:text-base">Player Level</h6>
                    </div>
                    <p className="text-gray-300 text-xs sm:text-sm ml-8">
                      If wins are equal, higher level players rank higher. Level represents overall experience and skill.
                    </p>
                    <p className="text-green-300 text-xs mt-2 ml-8">
                      💡 Example: Level 5 player ranks above Level 3 player if both have 2 wins
                    </p>
                  </div>

                  <div className="bg-slate-600 rounded-lg p-3 border-l-4 border-yellow-500">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl font-bold text-yellow-400">3</span>
                      <h6 className="font-bold text-yellow-300 text-sm sm:text-base">Win Rate Percentage</h6>
                    </div>
                    <p className="text-gray-300 text-xs sm:text-sm ml-8">
                      If level is also equal, players with higher overall win rate rank higher. Win rate is calculated from all battles (not just tournament).
                    </p>
                    <p className="text-yellow-300 text-xs mt-2 ml-8">
                      💡 Example: 60% win rate ranks above 45% win rate if both have same wins and level
                    </p>
                  </div>

                  <div className="bg-slate-600 rounded-lg p-3 border-l-4 border-orange-500">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl font-bold text-orange-400">4</span>
                      <h6 className="font-bold text-orange-300 text-sm sm:text-base">Total Battles Played</h6>
                    </div>
                    <p className="text-gray-300 text-xs sm:text-sm ml-8">
                      If all above are equal, players with more total battles rank higher. More experience = higher ranking.
                    </p>
                    <p className="text-orange-300 text-xs mt-2 ml-8">
                      💡 Example: 100 total battles ranks above 50 total battles if all other factors are equal
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 mt-4">
                <p className="text-sm text-yellow-300">
                  <strong>💡 Strategy Tip:</strong> To maximize your tournament ranking, focus on winning battles first, 
                  but also consider leveling up and maintaining a high win rate in regular battles. Every factor matters!
                </p>
              </div>
            </div>

            {/* Tournament Rewards */}
            <div className="bg-slate-800 rounded-lg p-4 sm:p-6 border border-slate-600">
              <h4 className="text-xl sm:text-2xl font-bold text-amber-400 mb-4">
                🎁 Tournament Rewards
              </h4>
              <p className="text-gray-300 mb-4 text-sm sm:text-base">
                Top performers in tournaments receive special rewards distributed <span style={{ color: '#1652F0' }}>on-chain</span>:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-yellow-900/30 to-amber-900/30 border-2 border-yellow-500 rounded-lg p-4 text-center">
                  <div className="text-4xl mb-2">🥇</div>
                  <h5 className="font-bold text-yellow-300 mb-2 text-sm sm:text-base">1st Place</h5>
                  <p className="text-gray-300 text-xs sm:text-sm">Highest rewards + Champion status</p>
                </div>
                <div className="bg-gradient-to-br from-gray-700/30 to-gray-600/30 border-2 border-gray-400 rounded-lg p-4 text-center">
                  <div className="text-4xl mb-2">🥈</div>
                  <h5 className="font-bold text-gray-300 mb-2 text-sm sm:text-base">2nd Place</h5>
                  <p className="text-gray-300 text-xs sm:text-sm">Strong rewards + Runner-up status</p>
                </div>
                <div className="bg-gradient-to-br from-orange-900/30 to-orange-800/30 border-2 border-orange-600 rounded-lg p-4 text-center">
                  <div className="text-4xl mb-2">🥉</div>
                  <h5 className="font-bold text-orange-300 mb-2 text-sm sm:text-base">3rd Place</h5>
                  <p className="text-gray-300 text-xs sm:text-sm">Good rewards + Bronze medal</p>
                </div>
              </div>

              <div className="bg-slate-700 rounded-lg p-4 mt-4 border border-slate-500">
                <p className="text-gray-300 text-xs sm:text-sm">
                  <strong>Note:</strong> Reward amounts and distribution are determined by the tournament contract. 
                  All rewards are distributed <span style={{ color: '#1652F0' }}>on-chain</span> and are verifiable on BaseScan.
                </p>
              </div>
            </div>

            {/* Tournament Tips */}
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700 rounded-lg p-4 sm:p-6">
              <h4 className="text-xl sm:text-2xl font-bold text-purple-300 mb-4">
                🎯 Tournament Strategy Tips
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs sm:text-sm">
                <div>
                  <h5 className="font-semibold text-purple-300 mb-2">💪 Preparation</h5>
                  <ul className="space-y-1 text-gray-300 list-disc list-inside">
                    <li>Ensure you have enough OGB tokens for all battles</li>
                    <li>Level up before joining to improve tie-breaker position</li>
                    <li>Equip your best buff NFTs for maximum advantage</li>
                    <li>Practice in regular PvP to improve your win rate</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-blue-300 mb-2">⚔️ During Tournament</h5>
                  <ul className="space-y-1 text-gray-300 list-disc list-inside">
                    <li>Stay active - don't miss your scheduled matches</li>
                    <li>Adapt your strategy based on opponent patterns</li>
                    <li>Use buffs strategically - save Frost for critical moments</li>
                    <li>Monitor the leaderboard to know your position</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800">
      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={onBackToMenu}
          className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-gray-200 rounded-lg transition-colors text-sm"
        >
          <ArrowLeft size={14} />
          <span className="hidden sm:inline">Back to Menu</span>
          <span className="sm:hidden">Back</span>
        </button>
      </div>

      {/* Header */}
      <header className="text-center mb-8 mt-16">
        <div className="bg-slate-800 rounded-lg shadow-xl border border-slate-600 p-4 sm:p-8 max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            📖 INTRODUCTION TO ORIGAMI BATTLES
          </h1>
          <p className="text-lg sm:text-xl text-gray-300">
            Everything you need to know to master the game, from core mechanics to blockchain integration.
          </p>
        </div>
      </header>

      {/* Main Content */}
      <div className="w-full max-w-6xl mx-auto bg-slate-800 rounded-lg shadow-xl border border-slate-600 p-4 sm:p-6 mb-8">
        {/* Tab Navigation - Mobile Responsive */}
        <div className="flex flex-wrap justify-center border-b border-slate-600 mb-6 sticky top-0 bg-slate-800/80 backdrop-blur z-10 py-2">
          <button
            className={`py-2 px-3 sm:py-3 sm:px-6 text-sm sm:text-lg font-semibold ${activeTab === 'overview' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-blue-300'}`}
            onClick={() => setActiveTab('overview')}
          >
            <span className="hidden sm:inline">Overview</span>
            <span className="sm:hidden">Overview</span>
          </button>
          <button
            className={`py-2 px-3 sm:py-3 sm:px-6 text-sm sm:text-lg font-semibold ${activeTab === 'mechanics' ? 'text-green-400 border-b-2 border-green-400' : 'text-gray-400 hover:text-green-300'}`}
            onClick={() => setActiveTab('mechanics')}
          >
            <span className="hidden sm:inline">Game Mechanics</span>
            <span className="sm:hidden">Mechanics</span>
          </button>
          <button
            className={`py-2 px-3 sm:py-3 sm:px-6 text-sm sm:text-lg font-semibold ${activeTab === 'blockchain' ? 'text-purple-400 border-b-2 border-purple-400' : 'text-gray-400 hover:text-purple-300'}`}
            onClick={() => setActiveTab('blockchain')}
          >
            <span className="hidden sm:inline">Blockchain</span>
            <span className="sm:hidden">Blockchain</span>
          </button>
          <button
            className={`py-2 px-3 sm:py-3 sm:px-6 text-sm sm:text-lg font-semibold ${activeTab === 'modes' ? 'text-orange-400 border-b-2 border-orange-400' : 'text-gray-400 hover:text-orange-300'}`}
            onClick={() => setActiveTab('modes')}
          >
            <span className="hidden sm:inline">Battle Modes</span>
            <span className="sm:hidden">Modes</span>
          </button>
          <button
            className={`py-2 px-3 sm:py-3 sm:px-6 text-sm sm:text-lg font-semibold ${activeTab === 'tournaments' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-400 hover:text-yellow-300'}`}
            onClick={() => setActiveTab('tournaments')}
          >
            <span className="hidden sm:inline">Tournaments</span>
            <span className="sm:hidden">Tournaments</span>
          </button>
        </div>

        {/* Content Area - Mobile Responsive */}
        <div className={`p-4 sm:p-6 bg-slate-900 rounded-lg border border-slate-700 ${
          activeTab === 'overview' ? 'mt-[434px] sm:mt-[300px]' : 
          activeTab === 'mechanics' ? 'mt-[2354px] sm:mt-[1912px]' :
          activeTab === 'blockchain' ? 'mt-[2900px] sm:mt-[2080px]' :
          activeTab === 'tournaments' ? 'mt-[2800px] sm:mt-[2200px]' :
          'mt-[425px] sm:mt-[395px]'
        }`}>
          {renderContent()}
        </div>
      </div>

      {/* Call to Action - Mobile Responsive */}
      <div className="bg-slate-800 rounded-lg shadow-xl border border-slate-600 text-center p-4 sm:p-8 max-w-4xl w-full mb-8">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
          Ready to Simulate a Battle?
        </h2>
        <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8">
          Start a 100% backend simulation battle. No wallet, no ETH, no confirmations — just the full battle engine experience.
        </p>
        <button
          onClick={onStartPlaying}
          className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-3 px-6 sm:py-4 sm:px-8 rounded-lg transition-all shadow-lg text-sm sm:text-base"
        >
          🚀 Start Simulation Battle
        </button>
      </div>

      {/* Footer */}
      <footer className="mt-4 text-center text-gray-400 text-xs sm:text-sm">
        <p>Origami Battles © 2025. All rights reserved.</p>
      </footer>
    </div>
  )
}
